import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Video, Upload, Trash2, Download, Play, Pause, 
  Film, Users, ShoppingBag, Percent, Loader2, X,
  FolderOpen, Eye, Calendar, FileVideo, Link, Copy, BarChart3, MousePointerClick
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VIDEO_CATEGORIES = [
  { id: 'buyer', label: 'Buyer Videos', icon: ShoppingBag, color: 'text-[#00F0FF]', bgColor: 'bg-[#00F0FF]/10' },
  { id: 'seller', label: 'Seller Videos', icon: Users, color: 'text-white', bgColor: 'bg-white text-black/10' },
  { id: 'affiliate', label: 'Affiliate Videos', icon: Percent, color: 'text-[#00F0FF]', bgColor: 'bg-[#00F0FF]/10' },
  { id: 'general', label: 'General/Promo', icon: Film, color: 'text-[#FF6B6B]', bgColor: 'bg-[#FF6B6B]/10' }
];

function MarketingVideos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(null);
  const fileInputRef = useRef(null);
  
  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    category: 'buyer',
    platform: 'all'
  });
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    loadVideos();
  }, []);

  const loadVideos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/marketing-videos/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVideos(response.data.videos || []);
    } catch (error) {
      console.error('Error loading videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('video/')) {
        toast.error('Please select a video file');
        return;
      }
      if (file.size > 100 * 1024 * 1024) { // 100MB limit
        toast.error('Video must be less than 100MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a video file');
      return;
    }
    if (!uploadForm.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('video', selectedFile);
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      formData.append('category', uploadForm.category);
      formData.append('platform', uploadForm.platform);

      await axios.post(`${API_URL}/api/marketing-videos/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Video uploaded successfully!');
      setShowUploadModal(false);
      setSelectedFile(null);
      setUploadForm({ title: '', description: '', category: 'buyer', platform: 'all' });
      loadVideos();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload video');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('Delete this marketing video?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/marketing-videos/${videoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Video deleted');
      loadVideos();
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  const filteredVideos = selectedCategory === 'all' 
    ? videos 
    : videos.filter(v => v.category === selectedCategory);

  const getCategoryInfo = (categoryId) => {
    return VIDEO_CATEGORIES.find(c => c.id === categoryId) || VIDEO_CATEGORIES[3];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF6B6B] to-[#00F0FF] flex items-center justify-center">
            <Video className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold">Marketing Videos</h2>
            <p className="text-sm text-neutral-400">Upload and manage promotional videos</p>
          </div>
        </div>
        <Button
          onClick={() => setShowUploadModal(true)}
          className="bg-white text-black hover:bg-neutral-200"
        >
          <Upload className="w-4 h-4 mr-2" />
          Upload Video
        </Button>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setSelectedCategory('all')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
            selectedCategory === 'all'
              ? 'bg-white text-black'
              : 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
          }`}
        >
          All Videos ({videos.length})
        </button>
        {VIDEO_CATEGORIES.map(cat => {
          const count = videos.filter(v => v.category === cat.id).length;
          const Icon = cat.icon;
          return (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                selectedCategory === cat.id
                  ? `${cat.bgColor} ${cat.color} border border-current`
                  : 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {cat.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Videos Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#00F0FF]" />
        </div>
      ) : filteredVideos.length === 0 ? (
        <div className="text-center py-12 bg-neutral-800/30 rounded-xl border border-white/5">
          <FolderOpen className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
          <p className="text-neutral-400 mb-4">
            {selectedCategory === 'all' 
              ? 'No marketing videos uploaded yet' 
              : `No ${getCategoryInfo(selectedCategory).label.toLowerCase()} uploaded yet`}
          </p>
          <Button
            onClick={() => setShowUploadModal(true)}
            variant="outline"
            className="border-white/30 text-white hover:bg-[#00F0FF]/10 hover:text-[#00F0FF] hover:border-[#00F0FF]/50"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Your First Video
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredVideos.map(video => {
            const catInfo = getCategoryInfo(video.category);
            const CatIcon = catInfo.icon;
            return (
              <div
                key={video.video_id}
                className="bg-neutral-800/50 border border-white/10 rounded-xl overflow-hidden group"
              >
                {/* Video Preview */}
                <div className="relative aspect-video bg-black">
                  {playingVideo === video.video_id ? (
                    <video
                      src={`${API_URL}${video.url}`}
                      className="w-full h-full object-contain"
                      controls
                      autoPlay
                      onEnded={() => setPlayingVideo(null)}
                    />
                  ) : (
                    <>
                      {video.thumbnail_url ? (
                        <img
                          src={`${API_URL}${video.thumbnail_url}`}
                          alt={video.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-neutral-800 to-neutral-900">
                          <FileVideo className="w-16 h-16 text-neutral-600" />
                        </div>
                      )}
                      <button
                        onClick={() => setPlayingVideo(video.video_id)}
                        className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                          <Play className="w-8 h-8 text-white ml-1" />
                        </div>
                      </button>
                    </>
                  )}
                  
                  {/* Category Badge */}
                  <div className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-medium ${catInfo.bgColor} ${catInfo.color} flex items-center gap-1`}>
                    <CatIcon className="w-3 h-3" />
                    {catInfo.label.replace(' Videos', '')}
                  </div>
                </div>

                {/* Video Info */}
                <div className="p-4">
                  <h3 className="font-semibold mb-1 truncate">{video.title}</h3>
                  {video.description && (
                    <p className="text-sm text-neutral-400 mb-3 line-clamp-2">{video.description}</p>
                  )}
                  
                  <div className="flex items-center justify-between text-xs text-neutral-500 mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(video.created_at)}
                    </span>
                    <span>{formatFileSize(video.file_size)}</span>
                  </div>

                  {/* Tracking Link Section */}
                  {video.tracking_url && (
                    <div className="mb-3 p-2 bg-neutral-900/80 rounded-lg border border-white/5">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-neutral-400 flex items-center gap-1">
                          <Link className="w-3 h-3" />
                          Tracking Link
                        </span>
                        {(video.clicks > 0 || video.views > 0) && (
                          <span className="text-xs text-green-400 flex items-center gap-1">
                            <MousePointerClick className="w-3 h-3" />
                            {video.clicks || 0} clicks
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="text-xs text-[#00F0FF] bg-black/30 px-2 py-1 rounded flex-1 truncate">
                          {window.location.origin}{video.tracking_url}
                        </code>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}${video.tracking_url}`);
                            toast.success('Tracking link copied!');
                          }}
                          className="p-1.5 bg-[#00F0FF]/10 hover:bg-[#00F0FF]/20 rounded text-[#00F0FF] transition-colors"
                          title="Copy tracking link"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                    <a
                      href={`${API_URL}${video.url}`}
                      download={video.filename}
                      className="flex-1"
                    >
                      <Button size="sm" variant="outline" className="w-full border-white/20">
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </Button>
                    </a>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(video.video_id)}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-neutral-900 border border-white/10 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <Upload className="w-5 h-5 text-white" />
                Upload Marketing Video
              </h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFile(null);
                }}
                className="text-neutral-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium mb-2">Video File *</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                {selectedFile ? (
                  <div className="bg-neutral-800 border border-white/10 rounded-xl p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileVideo className="w-8 h-8 text-white" />
                      <div>
                        <p className="font-medium truncate max-w-[200px]">{selectedFile.name}</p>
                        <p className="text-xs text-neutral-400">{formatFileSize(selectedFile.size)}</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSelectedFile(null)}
                      className="text-neutral-400 hover:text-white"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full border-2 border-dashed border-white/20 rounded-xl p-8 text-center hover:border-white/50 transition-colors"
                  >
                    <Upload className="w-10 h-10 text-neutral-500 mx-auto mb-3" />
                    <p className="text-neutral-300 mb-1">Click to select video</p>
                    <p className="text-xs text-neutral-500">MP4, MOV, WebM (max 100MB)</p>
                  </button>
                )}
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium mb-2">Title *</label>
                <Input
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                  placeholder="e.g., Buyer Welcome Video"
                  className="bg-neutral-800 border-white/10"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <Textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  placeholder="Brief description of this video..."
                  className="bg-neutral-800 border-white/10 min-h-[80px]"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium mb-2">Category *</label>
                <div className="grid grid-cols-2 gap-2">
                  {VIDEO_CATEGORIES.map(cat => {
                    const Icon = cat.icon;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => setUploadForm({ ...uploadForm, category: cat.id })}
                        className={`p-3 rounded-xl border text-left flex items-center gap-2 transition-all ${
                          uploadForm.category === cat.id
                            ? `${cat.bgColor} ${cat.color} border-current`
                            : 'border-white/10 hover:border-white/20'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="text-sm font-medium">{cat.label.replace(' Videos', '')}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Platform */}
              <div>
                <label className="block text-sm font-medium mb-2">Target Platform</label>
                <select
                  value={uploadForm.platform}
                  onChange={(e) => setUploadForm({ ...uploadForm, platform: e.target.value })}
                  className="w-full bg-neutral-800 border border-white/10 rounded-lg px-4 py-2 text-white"
                >
                  <option value="all">All Platforms</option>
                  <option value="tiktok">TikTok</option>
                  <option value="instagram">Instagram</option>
                  <option value="facebook">Facebook</option>
                  <option value="youtube">YouTube</option>
                  <option value="website">Website Only</option>
                </select>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedFile(null);
                  }}
                  className="flex-1 border-white/20"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpload}
                  disabled={uploading || !selectedFile || !uploadForm.title.trim()}
                  className="flex-1 bg-white text-black hover:bg-neutral-200"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Video
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MarketingVideos;
